import { ExternalLink, FileSearch, Trash2, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ConstraintBadges } from './constraint-badges';
import { OtherConstraintsList } from './other-constraints-list';
import { SubScoreBadge } from './sub-score-badge';
import type { SuggestedOpportunity } from '../types/suggested-opportunities.types';

interface SuggestedOpportunityCardProps {
  ao: SuggestedOpportunity;
  onImport?: (id: number) => void;
  onExclude?: (id: number) => void;
  onViewDetail?: (id: number) => void;
  onVerifyEligibility?: (id: number) => void;
}

const SCORE_COLOR_CLASSES = {
  strong: 'border-l-4 border-l-emerald-500',
  moderate: 'border-l-4 border-l-amber-500',
  weak: 'border-l-4 border-l-rose-400',
  none: 'border-l-4 border-l-muted',
} as const;

function scoreClass(score: number | null) {
  if (score === null) return SCORE_COLOR_CLASSES.none;
  if (score >= 70) return SCORE_COLOR_CLASSES.strong;
  if (score >= 40) return SCORE_COLOR_CLASSES.moderate;
  return SCORE_COLOR_CLASSES.weak;
}

function formatAmount(amount: string | null): string {
  if (!amount) return '—';
  const n = parseFloat(amount);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
  if (n >= 1_000) return `${Math.round(n / 1_000)} K€`;
  return `${n.toFixed(0)} €`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

export function SuggestedOpportunityCard({
  ao,
  onImport,
  onExclude,
  onViewDetail,
  onVerifyEligibility,
}: SuggestedOpportunityCardProps) {
  const detail = ao.autoSuggestionScoreDetail;
  const globalScore = ao.autoSuggestionScore;

  return (
    <Card className={cn('p-4 space-y-3', scoreClass(globalScore))}>
      {/* En-tête : score + titre */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div
            className={cn(
              'shrink-0 flex items-center justify-center w-12 h-12 rounded-md font-mono font-bold text-lg',
              globalScore === null
                ? 'bg-muted text-muted-foreground'
                : globalScore >= 70
                  ? 'bg-emerald-100 text-emerald-800'
                  : globalScore >= 40
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-rose-100 text-rose-800',
            )}
          >
            {globalScore ?? '—'}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-medium leading-tight line-clamp-2">{ao.title}</h3>
            {ao.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {ao.description}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              <span className="font-mono">{ao.externalId}</span>
              <span className="mx-1">·</span>
              <span className="uppercase">{ao.importSource}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Sprint 5b.1.6 — Badges contraintes fortes (réservé ESS, défense, etc.) */}
      {ao.enrichment && <ConstraintBadges enrichment={ao.enrichment} />}

      {/* Sous-scores en badges */}
      {detail && (
        <div className="flex flex-wrap gap-1.5">
          <SubScoreBadge
            dimension="sectoral"
            score={detail.sectoral}
            reason={detail.sectoralReason}
            details={{
              effectiveCpvCount: detail.effectiveCpvCount,
              matchingCpvCount: detail.matchingCpvCount,
            }}
            enrichmentReasons={detail.enrichmentReasons}
          />
          <SubScoreBadge
            dimension="geographic"
            score={detail.geographic}
            reason={detail.geographicReason}
            enrichmentReasons={detail.enrichmentReasons}
          />
          <SubScoreBadge
            dimension="amount"
            score={detail.amount}
            reason={detail.amountReason}
          />
          <SubScoreBadge
            dimension="marketType"
            score={detail.marketType}
            reason={detail.marketTypeReason}
          />
        </div>
      )}

      {/* Sprint 5b.1.6 — Autres contraintes textuelles libres (max 3 + « +N autres ») */}
      {ao.enrichment && ao.enrichment.otherConstraints.length > 0 && (
        <OtherConstraintsList constraints={ao.enrichment.otherConstraints} />
      )}

      {/* Métadonnées AO */}
      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
        <span>
          <strong className="text-foreground">{formatAmount(ao.estimatedAmount)}</strong>
        </span>
        {ao.geoDepartement && (
          <span>
            {ao.geoDepartement.name} ({ao.geoDepartement.code})
          </span>
        )}
        {ao.marketType && (
          <Badge variant="outline" className="h-4 text-[10px] px-1.5 font-normal">
            {ao.marketType}
          </Badge>
        )}
        {ao.submissionDeadline && (
          <span>Échéance {formatDate(ao.submissionDeadline)}</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" variant="default" onClick={() => onImport?.(ao.id)}>
          <Upload className="h-3.5 w-3.5 mr-1" />
          Importer
        </Button>
        <Button size="sm" variant="outline" onClick={() => onVerifyEligibility?.(ao.id)}>
          <FileSearch className="h-3.5 w-3.5 mr-1" />
          Vérifier l'éligibilité
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onViewDetail?.(ao.id)}>
          <ExternalLink className="h-3.5 w-3.5 mr-1" />
          Détail AO
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onExclude?.(ao.id)} className="text-muted-foreground">
          <Trash2 className="h-3.5 w-3.5 mr-1" />
          Exclure
        </Button>
      </div>
    </Card>
  );
}
